import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, X, Linkedin, Twitter, Globe, Github } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePodcastHostDialog } from '@/contexts/PodcastHostDialogContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { usePodcastHosts, PodcastHostInsert } from '@/hooks/usePodcastHosts';
import { RichTextEditor } from '@/components/learn/admin/RichTextEditor';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const hostSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  slug: z.string().optional(),
  title: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  bio: z.string().optional(),
  image_url: z.string().optional(),
  linkedin: z.string().optional(),
  twitter: z.string().optional(),
  website: z.string().optional(),
  github: z.string().optional(),
});

type HostFormData = z.infer<typeof hostSchema>;

export const GlobalPodcastHostDialog = () => {
  const { isOpen, mode, editingHost, closeDialog } = usePodcastHostDialog();
  const { currentBrandId } = useBrandManager();
  const { createHost, updateHost } = usePodcastHosts(currentBrandId);
  const [isUploading, setIsUploading] = useState(false);
  const isCreate = mode === 'create';

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const form = useForm<HostFormData>({
    resolver: zodResolver(hostSchema),
    defaultValues: {
      name: '',
      slug: '',
      title: '',
      email: '',
      bio: '',
      image_url: '',
      linkedin: '',
      twitter: '',
      website: '',
      github: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && editingHost) {
        const socialLinks = editingHost.social_links as Record<string, string> | null;
        form.reset({
          name: editingHost.name || '',
          slug: editingHost.slug || '',
          title: editingHost.title || '',
          email: editingHost.email || '',
          bio: editingHost.bio || '',
          image_url: editingHost.image_url || '',
          linkedin: socialLinks?.linkedin || '',
          twitter: socialLinks?.twitter || '',
          website: socialLinks?.website || '',
          github: socialLinks?.github || '',
        });
      } else {
        form.reset({
          name: '',
          slug: '',
          title: '',
          email: '',
          bio: '',
          image_url: '',
          linkedin: '',
          twitter: '',
          website: '',
          github: '',
        });
      }
    }
  }, [isOpen, mode, editingHost, form]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop() || 'png';
      const fileName = `${currentBrandId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('instructor-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('instructor-images')
        .getPublicUrl(fileName);

      form.setValue('image_url', publicUrl);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    form.setValue('image_url', '');
  };

  const handleNameChange = (name: string) => {
    form.setValue('name', name);
    const currentSlug = form.getValues('slug');
    if (!currentSlug) {
      form.setValue('slug', generateSlug(name));
    }
  };

  const onSubmit = async (data: HostFormData) => {
    if (!data.name) return;

    const social_links = {
      linkedin: data.linkedin || undefined,
      twitter: data.twitter || undefined,
      website: data.website || undefined,
      github: data.github || undefined,
    };

    const hostData = {
      name: data.name,
      slug: data.slug || generateSlug(data.name),
      title: data.title || null,
      email: data.email || null,
      bio: data.bio || null,
      image_url: data.image_url || null,
      social_links,
    };

    if (mode === 'create') {
      if (!currentBrandId) return;
      await createHost.mutateAsync({
        ...hostData,
        brand_id: currentBrandId,
        is_active: true,
      } as PodcastHostInsert);
    } else {
      if (!editingHost) return;
      await updateHost.mutateAsync({
        id: editingHost.id,
        ...hostData,
      });
    }

    closeDialog();
  };

  const isPending = createHost.isPending || updateHost.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isCreate ? 'Create Host' : 'Edit Host'} 
            popupContext="/podcasthost/hosts"
            isOpen={isOpen}
          >
            {isCreate ? 'Create New Host' : 'Edit Host'}
          </DialogTitleWithBug>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="social">Social Links</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6 mt-4">
                {/* Image */}
                <FormField
                  control={form.control}
                  name="image_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Profile Image</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-20 w-20">
                              <AvatarImage src={field.value || undefined} />
                              <AvatarFallback>
                                {form.watch('name')?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'HO'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 space-y-2">
                              {field.value ? (
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRemoveImage}
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Remove Image
                                  </Button>
                                  <p className="text-xs text-muted-foreground">Image uploaded</p>
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    id="host-image-upload"
                                    disabled={isUploading}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => document.getElementById('host-image-upload')?.click()}
                                    disabled={isUploading}
                                  >
                                    <Upload className="h-4 w-4 mr-2" />
                                    {isUploading ? 'Uploading...' : 'Upload Image'}
                                  </Button>
                                  <p className="text-xs text-muted-foreground">
                                    PNG, JPG up to 5MB
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <Input
                            placeholder="Or paste image URL"
                            {...field}
                            className="text-sm"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="John Doe" 
                          {...field}
                          onChange={(e) => handleNameChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Slug */}
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Slug</FormLabel>
                      <FormControl>
                        <Input placeholder="url-friendly-name" {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1">Used in URLs. Auto-generated from name if left empty.</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Title */}
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title/Role</FormLabel>
                      <FormControl>
                        <Input placeholder="Lead Host, Senior Producer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="host@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Bio */}
                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bio</FormLabel>
                      <FormControl>
                        <RichTextEditor
                          content={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Tell us about this host..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="social" className="space-y-6 mt-4">
                {/* LinkedIn */}
                <FormField
                  control={form.control}
                  name="linkedin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Linkedin className="h-4 w-4" />
                        LinkedIn
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="https://linkedin.com/in/..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Twitter/X */}
                <FormField
                  control={form.control}
                  name="twitter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Twitter className="h-4 w-4" />
                        Twitter/X
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="https://twitter.com/..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Website */}
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Website
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* GitHub */}
                <FormField
                  control={form.control}
                  name="github"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Github className="h-4 w-4" />
                        GitHub
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="https://github.com/..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={!form.watch('name') || isPending}>
                {isPending ? 'Saving...' : isCreate ? 'Create' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
